import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AppSidebar, { MobileTopBar, getInitials } from "@/components/AppSidebar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Search, ShieldCheck, GraduationCap, Lock, Unlock, MoreVertical, UserCog, UserMinus,
  UserCheck, UserX, Trash2, History, ChevronLeft, ChevronRight, X as XIcon,
  MessagesSquare, Eye, Paperclip,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "react-hot-toast";
import { format, parseISO, isValid } from "date-fns";

const navItems = (activeTab, setActiveTab) => [
  { icon: ShieldCheck, label: "Users", active: activeTab === "users", onClick: () => setActiveTab("users") },
  { icon: History, label: "Activity Log", active: activeTab === "activity", onClick: () => setActiveTab("activity") },
  { icon: MessagesSquare, label: "Conversations", active: activeTab === "conversations", onClick: () => setActiveTab("conversations") },
];

// Group action types by app area for a quick-scan color badge — falls back to slate for anything unmapped.
const ACTION_COLOR_GROUPS = [
  { prefix: "admin_", cls: "bg-rose-100 text-rose-700" },
  { prefix: "community_", cls: "bg-teal-100 text-teal-700" },
  { prefix: "guide_", cls: "bg-emerald-100 text-emerald-700" },
  { prefix: "wednesday_", cls: "bg-indigo-100 text-indigo-700" },
  { prefix: "user_", cls: "bg-slate-200 text-slate-700" },
  { prefix: "maintenance_", cls: "bg-orange-100 text-orange-700" },
];
const actionBadgeClass = (actionType) => {
  const match = ACTION_COLOR_GROUPS.find((g) => actionType?.startsWith(g.prefix));
  return match ? match.cls : "bg-amber-100 text-amber-700";
};
const formatActionType = (actionType) =>
  (actionType || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const formatActivityDate = (str) => {
  if (!str) return "";
  const d = parseISO(str);
  return isValid(d) ? format(d, "MMM d, yyyy 'at' h:mm a") : str;
};

export default function Admin({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Activity Log state
  const [activities, setActivities] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [actionTypes, setActionTypes] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityUserFilter, setActivityUserFilter] = useState("all");
  const [activityTypeFilter, setActivityTypeFilter] = useState("all");
  const [activityLoading, setActivityLoading] = useState(false);
  const ACTIVITY_PAGE_SIZE = 25;

  const fetchActivities = useCallback(() => {
    setActivityLoading(true);
    const params = { page: activityPage, limit: ACTIVITY_PAGE_SIZE };
    if (activityUserFilter !== "all") params.user_id = activityUserFilter;
    if (activityTypeFilter !== "all") params.action_type = activityTypeFilter;
    axios
      .get(`${API}/admin/activities`, { params })
      .then((res) => {
        setActivities(res.data.activities);
        setActivityTotal(res.data.total);
        setActionTypes(res.data.action_types);
      })
      .catch(() => toast.error("Failed to load activity log"))
      .finally(() => setActivityLoading(false));
  }, [activityPage, activityUserFilter, activityTypeFilter]);

  useEffect(() => {
    if (activeTab === "activity") fetchActivities();
  }, [activeTab, fetchActivities]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setActivityPage(1);
  }, [activityUserFilter, activityTypeFilter]);

  const totalActivityPages = Math.max(1, Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE));

  // Conversations oversight state
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationSearch, setConversationSearch] = useState("");
  const [openThread, setOpenThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== "conversations") return;
    setConversationsLoading(true);
    axios.get(`${API}/admin/conversations`)
      .then((res) => setConversations(res.data))
      .catch(() => toast.error("Failed to load conversations"))
      .finally(() => setConversationsLoading(false));
  }, [activeTab]);

  const openConversationThread = (thread) => {
    setOpenThread(thread);
    setThreadLoading(true);
    axios.get(`${API}/admin/conversations/${thread.user_a_id}/${thread.user_b_id}`)
      .then((res) => setThreadMessages(res.data))
      .catch(() => toast.error("Failed to load this conversation"))
      .finally(() => setThreadLoading(false));
  };

  const filteredConversations = useMemo(() => {
    const q = conversationSearch.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) => c.user_a_name?.toLowerCase().includes(q) || c.user_b_name?.toLowerCase().includes(q)
    );
  }, [conversations, conversationSearch]);

  useEffect(() => {
    if (user && !user.is_super_admin) {
      navigate("/home");
      return;
    }
    axios
      .get(`${API}/admin/users`)
      .then((res) => setUsers(res.data))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleIntensive = async (targetUser, nextValue) => {
    setUpdatingId(targetUser.user_id);
    try {
      await axios.put(`${API}/admin/users/${targetUser.user_id}/intensive-status`, {
        is_intensive_student: nextValue,
      });
      setUsers((prev) =>
        prev.map((u) => (u.user_id === targetUser.user_id ? { ...u, is_intensive_student: nextValue } : u))
      );
      toast.success(`Intensive access ${nextValue ? "granted" : "revoked"} for ${targetUser.name}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update access");
    } finally {
      setUpdatingId(null);
    }
  };

  const changeRole = async (targetUser) => {
    setUpdatingId(targetUser.user_id);
    const promoting = targetUser.role === "student";
    try {
      await axios.put(`${API}/users/${targetUser.user_id}/${promoting ? "upgrade-to-teacher" : "demote-to-student"}`);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === targetUser.user_id ? { ...u, role: promoting ? "teacher" : "student" } : u))
      );
      toast.success(`${targetUser.name} is now a ${promoting ? "teacher" : "student"}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to change role");
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleActive = async (targetUser) => {
    setUpdatingId(targetUser.user_id);
    const activating = !targetUser.is_active;
    try {
      await axios.put(`${API}/users/${targetUser.user_id}/${activating ? "set-active" : "set-inactive"}`);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === targetUser.user_id ? { ...u, is_active: activating } : u))
      );
      toast.success(`${targetUser.name} is now ${activating ? "active" : "inactive"}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deleteTarget.user_id}`);
      setUsers((prev) => prev.filter((u) => u.user_id !== deleteTarget.user_id));
      toast.success(`${deleteTarget.name} and all their data have been deleted`);
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  if (user && !user.is_super_admin) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="admin" brandLabel="Admin" navItems={navItems(activeTab, setActiveTab)} />
      <MobileTopBar brandLabel="Admin" />

      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          {activeTab === "users" ? (
          <>
          <div>
            <p className="text-xs font-semibold text-rose-600 uppercase tracking-widest mb-2">Admin</p>
            <h1 className="font-serif text-3xl font-bold text-slate-900 mb-1">Manage Users</h1>
            <p className="text-sm text-slate-500">
              Control Discipleship Intensive access, roles, and account status for everyone on the platform.
              New sign-ups never get Intensive access automatically — you have to turn it on here.
            </p>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="admin-user-search"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2" data-testid="admin-user-list">
              {filtered.length === 0 && (
                <p className="text-sm text-slate-500 py-8 text-center">No users match your search.</p>
              )}
              {filtered.map((u) => {
                const alwaysHasAccess = u.role === "teacher" || u.is_super_admin;
                const hasAccess = alwaysHasAccess || u.is_intensive_student;
                return (
                  <Card key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-slate-100">
                    <CardContent className="p-4 flex items-center gap-4">
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                          {getInitials(u.name)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900 truncate">{u.name}</p>
                          {u.is_super_admin && (
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px]">
                              Super Admin
                            </Badge>
                          )}
                          {!u.is_super_admin && u.role === "teacher" && (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">
                              Teacher
                            </Badge>
                          )}
                          {!u.is_active && (
                            <Badge variant="outline" className="border-orange-300 text-orange-600 bg-orange-50 text-[10px]">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className={`text-xs font-medium flex items-center gap-1 justify-end ${hasAccess ? "text-emerald-600" : "text-slate-400"}`}>
                            {hasAccess ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                            {alwaysHasAccess ? "Always on" : hasAccess ? "Has access" : "No access"}
                          </p>
                          {alwaysHasAccess && (
                            <p className="text-[10px] text-slate-400 flex items-center gap-1 justify-end">
                              <GraduationCap className="w-3 h-3" /> Staff role
                            </p>
                          )}
                        </div>
                        <Switch
                          data-testid={`admin-intensive-toggle-${u.user_id}`}
                          checked={hasAccess}
                          disabled={alwaysHasAccess || updatingId === u.user_id}
                          onCheckedChange={(val) => toggleIntensive(u, val)}
                        />

                        {u.is_super_admin || u.user_id === user?.user_id ? (
                          <div className="w-8 h-8 shrink-0" />
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                data-testid={`admin-user-menu-${u.user_id}`}
                                variant="ghost"
                                size="icon"
                                disabled={updatingId === u.user_id}
                                className="h-8 w-8 text-slate-400 hover:text-slate-700"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                data-testid={`admin-change-role-${u.user_id}`}
                                onClick={() => changeRole(u)}
                              >
                                {u.role === "student" ? (
                                  <><UserCog className="w-4 h-4 mr-2" /> Promote to Teacher</>
                                ) : (
                                  <><UserMinus className="w-4 h-4 mr-2" /> Demote to Student</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`admin-toggle-active-${u.user_id}`}
                                onClick={() => toggleActive(u)}
                              >
                                {u.is_active ? (
                                  <><UserX className="w-4 h-4 mr-2" /> Deactivate</>
                                ) : (
                                  <><UserCheck className="w-4 h-4 mr-2" /> Activate</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`admin-delete-user-${u.user_id}`}
                                onClick={() => setDeleteTarget(u)}
                                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          </>
          ) : activeTab === "activity" ? (
          <ActivityLogTab
            activities={activities}
            activityTotal={activityTotal}
            actionTypes={actionTypes}
            users={users}
            activityLoading={activityLoading}
            activityPage={activityPage}
            setActivityPage={setActivityPage}
            totalActivityPages={totalActivityPages}
            activityUserFilter={activityUserFilter}
            setActivityUserFilter={setActivityUserFilter}
            activityTypeFilter={activityTypeFilter}
            setActivityTypeFilter={setActivityTypeFilter}
          />
          ) : (
          <ConversationsTab
            conversations={filteredConversations}
            loading={conversationsLoading}
            search={conversationSearch}
            setSearch={setConversationSearch}
            onOpenThread={openConversationThread}
          />
          )}
        </div>
      </div>

      <Dialog open={!!openThread} onOpenChange={(open) => { if (!open) { setOpenThread(null); setThreadMessages(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="conversation-thread-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-rose-600" />
              {openThread?.user_a_name} &harr; {openThread?.user_b_name}
            </DialogTitle>
            <DialogDescription>Read-only — super admin oversight view</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-slate-50">
            {threadLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
              </div>
            ) : (
              threadMessages?.messages?.map((m) => {
                const fromA = m.sender_id === openThread.user_a_id;
                return (
                  <div key={m.message_id} className={`flex ${fromA ? "justify-start" : "justify-end"}`} data-testid={`thread-message-${m.message_id}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${fromA ? "bg-white border border-slate-200" : "bg-rose-100"}`}>
                      <p className="text-xs font-semibold text-slate-500 mb-0.5">{m.sender_name}</p>
                      {m.content && <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.content}</p>}
                      {m.checkin_preview && (
                        <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> Attached check-in ({m.checkin_preview.week_start})
                        </p>
                      )}
                      {m.attachments?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> {m.attachments.length} file{m.attachments.length === 1 ? "" : "s"}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">{formatActivityDate(m.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
            {!threadLoading && threadMessages?.messages?.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No messages in this thread.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes their account and all associated data (journals, submissions,
              messages, check-ins, posts, etc.). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel-btn" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-btn"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const ActivityLogTab = ({
  activities, activityTotal, actionTypes, users, activityLoading,
  activityPage, setActivityPage, totalActivityPages,
  activityUserFilter, setActivityUserFilter, activityTypeFilter, setActivityTypeFilter,
}) => {
  const hasFilters = activityUserFilter !== "all" || activityTypeFilter !== "all";
  return (
    <>
      <div>
        <p className="text-xs font-semibold text-rose-600 uppercase tracking-widest mb-2">Admin</p>
        <h1 className="font-serif text-3xl font-bold text-slate-900 mb-1">Activity Log</h1>
        <p className="text-sm text-slate-500">
          Every login, admin action, and app activity across Community, Weekly Guide, Wednesday Nights, and Discipleship Intensive — {activityTotal} total.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={activityUserFilter} onValueChange={setActivityUserFilter}>
          <SelectTrigger data-testid="activity-user-filter" className="w-[200px]">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activityTypeFilter} onValueChange={setActivityTypeFilter}>
          <SelectTrigger data-testid="activity-type-filter" className="w-[220px]">
            <SelectValue placeholder="All Action Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Action Types</SelectItem>
            {actionTypes.map((t) => (
              <SelectItem key={t} value={t}>{formatActionType(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button
            data-testid="activity-clear-filters-btn"
            variant="ghost"
            size="sm"
            className="text-slate-500"
            onClick={() => { setActivityUserFilter("all"); setActivityTypeFilter("all"); }}
          >
            <XIcon className="w-3.5 h-3.5 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {activityLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2" data-testid="activity-log-list">
          {activities.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">No activity matches these filters.</p>
          )}
          {activities.map((a) => (
            <Card key={a.activity_id} data-testid={`activity-row-${a.activity_id}`} className="border-slate-100">
              <CardContent className="p-4 flex items-start gap-3">
                <Badge className={`${actionBadgeClass(a.action_type)} hover:${actionBadgeClass(a.action_type)} shrink-0 text-[10px] mt-0.5`}>
                  {formatActionType(a.action_type)}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800">{a.description}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{a.user_name} &middot; {formatActivityDate(a.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalActivityPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            data-testid="activity-prev-page-btn"
            variant="outline"
            size="sm"
            disabled={activityPage <= 1}
            onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <p className="text-xs text-slate-400" data-testid="activity-page-indicator">
            Page {activityPage} of {totalActivityPages}
          </p>
          <Button
            data-testid="activity-next-page-btn"
            variant="outline"
            size="sm"
            disabled={activityPage >= totalActivityPages}
            onClick={() => setActivityPage((p) => Math.min(totalActivityPages, p + 1))}
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </>
  );
};


const ConversationsTab = ({ conversations, loading, search, setSearch, onOpenThread }) => (
  <>
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="conversations-search-input"
          placeholder="Search by name..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <p className="text-xs text-slate-400 self-center">
        Read-only oversight — opening a thread is logged to the Activity Log.
      </p>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
      </div>
    ) : conversations.length === 0 ? (
      <div className="text-center py-16 text-slate-400" data-testid="conversations-empty-state">
        No conversations found.
      </div>
    ) : (
      <div className="space-y-2" data-testid="conversations-list">
        {conversations.map((c) => (
          <button
            key={`${c.user_a_id}-${c.user_b_id}`}
            onClick={() => onOpenThread(c)}
            data-testid={`conversation-row-${c.user_a_id}-${c.user_b_id}`}
            className="w-full text-left flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-rose-300 hover:shadow-sm transition-all"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800">
                {c.user_a_name} <span className="text-slate-300 mx-1">&harr;</span> {c.user_b_name}
              </p>
              <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message_preview || "No messages yet"}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge variant="outline" className="text-slate-500">{c.message_count} msg{c.message_count === 1 ? "" : "s"}</Badge>
              <span className="text-xs text-slate-400 hidden sm:inline">{formatActivityDate(c.last_message_at)}</span>
              <Eye className="w-4 h-4 text-slate-300" />
            </div>
          </button>
        ))}
      </div>
    )}
  </>
);
